import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const InviteModal = ({ onClose, isAuthenticated = false, onSignIn, roomId }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch room-specific invite URL when authenticated and roomId is available
  useEffect(() => {
    const fetchInviteUrl = async () => {
      if (!isAuthenticated || !roomId) {
        // Fallback to static URL for unauthenticated or missing roomId
        setInviteUrl('window.location.origin');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('🔗 Fetching invite URL for room:', roomId);
        
        const response = await api.getRoomInviteLink(roomId);
        
        if (response.inviteUrl) {
          setInviteUrl(response.inviteUrl);
          console.log('✅ Got room invite URL:', response.inviteUrl);
        } else {
          throw new Error('No invite URL in response');
        }
      } catch (err) {
        console.error('❌ Error fetching invite URL:', err);
        setError('Failed to get invite link');
        // Fallback to static URL on error
        setInviteUrl('window.location.origin');
      } finally {
        setLoading(false);
      }
    };

    fetchInviteUrl();
  }, [isAuthenticated, roomId]);

  // Platform detection function
  const getPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) return 'iOS';
    if (/android/.test(userAgent)) return 'Android';
    if (/macintosh|mac os/.test(userAgent)) return 'Mac';
    if (/windows/.test(userAgent)) return 'Windows';
    return 'Unknown';
  };

  const platform = getPlatform();

  // Copy to clipboard function
  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = inviteUrl;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 border border-gray-700">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Invite Friends</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xl"
          >
            ×
          </button>
        </div>

        {/* Content - varies by authentication status */}
        <div className="space-y-4">
          {isAuthenticated ? (
            // Authenticated user content
            <>
              {/* Platform Display */}
              <div className="bg-gray-900 rounded p-3">
                <p className="text-gray-300 text-sm mb-1">Detected Platform:</p>
                <p className="text-white font-medium">Platform: {platform}</p>
              </div>

              {/* URL Display with Copy Button */}
              <div className="bg-gray-900 rounded p-3">
                <p className="text-gray-300 text-sm mb-2">Invite URL:</p>
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <p className="text-gray-400 text-sm">Loading invite link...</p>
                  </div>
                ) : error ? (
                  <div className="flex items-center space-x-2">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <p className="text-blue-400 font-mono text-sm break-all flex-1">{inviteUrl}</p>
                    <button
                      onClick={handleCopy}
                      disabled={!inviteUrl}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        copySuccess 
                          ? 'bg-green-600 text-white' 
                          : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-500 disabled:cursor-not-allowed'
                      }`}
                    >
                      {copySuccess ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            // Guest user content
            <div className="bg-gray-900 rounded p-4 text-center">
              <div className="mb-3">
                <span className="text-4xl">👥</span>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Login Required</h3>
              <p className="text-gray-300 text-sm mb-4">
                You need to be logged in to invite friends to join you.
              </p>
              <button
                onClick={() => {
                  onClose(); // Close invite modal first
                  onSignIn(); // Then open sign in modal
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-colors"
              >
                Sign In
              </button>
            </div>
          )}
        </div>

        {/* Footer - only show for authenticated users */}
        {isAuthenticated && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteModal;